// Filename: uicanvasportal.js
// Timestamp: 2018.12.10-11:42:39 (last modified)
// Author(s): bumblehead <chris@bumblehead.com>
//
// set width and height of canvas.parent
// ensure both canvas and canvas.parent have width and height

import uimesh from './uimesh.js'
import domwh from 'domwh'
import touchboom from 'touchboom'

import {
  getglrenderer,
  getperspectivecamera,
  getyawobj,
  disposeNode,
  render
} from './uithree.js'

import {
  getglcanvaswh,
  getresetglcanvas
} from './uicanvas.js'

import {
  unit_pixels_as_degrees,
  unit_degrees_as_radians,
  unit_degrees_as_pixels,
  unit_pixel_weightelem,
  unit_key_weight
} from './bumble_unit.js'

import uicanvasstage from './uicanvasstage.js'

import {
  gnpublish_subj
} from '@bumblehead/gani/src/gnpublish.js'

import {
  gndom_nodeelem
} from '@bumblehead/gani/src/gndom.js'

import canv from '@bumblehead/gani/src/basic/canv.js'

const PRJ_CYLINDER = 'cylinder'
const PRJ_SPHERE = 'spherical'
const PRJ_PLANE = 'plane'
const PRJ_CUBE = 'cube'

const xvnode = (sess, cfg, graph, node, pg) => {
  const canvas = cfg.h(pg, node).canvas

  return (
    canvas('.:ui-glcanvas.vrcanvas#:key---glcanvas', {
      tabindex: '0' // focusable
    })
  )
}

const rotationxyIsSame = (rotxya, rotxyb) => (
  rotxya[0] === rotxyb[0] && rotxya[1] === rotxyb[1])

const pixeltodegreearr = (arr, pw) => (
  arr.map(xy => unit_pixels_as_degrees(xy, pw)))

const getlocalrotation = (sess, cfg, graph, node, rafcfg) => {
  const totalxy = touchboom.coordsgettotal(rafcfg)
  const degxy = pixeltodegreearr(totalxy, rafcfg.pw)

  return {
    rotationxy: degxy
  }
}
/*
const updatescene = (sess, cfg, graph, node, rafcfg) => (
  gnpublish_subj(sess, cfg, node.get('key'), (
    getlocalrotation(sess, cfg, graph, node, rafcfg))));
*/
const isresized = (sess, cfg, graph, node, rafcfg) => {
  const curwh = getglcanvaswh(cfg, node)
  const oldwh = rafcfg.wharr || []

  return Boolean(curwh[0] !== oldwh[0] || curwh[1] !== oldwh[1])
}

const getskygeometry = (THREE, projection, isflip) => {
  let geometry = null

  projection = String(projection).toLowerCase()

  if (projection === PRJ_SPHERE) {
    geometry = new THREE.SphereGeometry(400, 30, 30)
  } else if (projection === PRJ_CYLINDER) {
    geometry = new THREE.CylinderGeometry(256, 256, 256, 50, 50, true)
  } else if (projection === PRJ_CUBE) {
    // 'cubegeometry', no longer supported
    // geometry = new THREE.CubeGeometry(128, 128, 128);
  } else if (projection === PRJ_PLANE) {
    geometry = new THREE.PlaneGeometry(480, 204, 4, 4)
  }

  if (geometry && isflip) { // flip uvs inside shape
    geometry.applyMatrix(new THREE.Matrix4().makeScale(-1, 1, 1))
  }

  return geometry
}

const getscenemesh = (THREE, geometry, movieMaterial) => {
  const scenemesh = new THREE.Mesh(geometry, movieMaterial)

  scenemesh.position.set(0, 0, 0)
  scenemesh.name = 'projection'

  return scenemesh
}

const getcoords = (cfg, node, pg, rotationxy) => {
  const canvaselem = getresetglcanvas(cfg, node)
  const pw = unit_pixel_weightelem(canvaselem)

  return touchboom.coords([ {
    autoweight: unit_key_weight(),
    bgn: unit_degrees_as_pixels(rotationxy[0], pw)
  }, {
    autoweight: unit_key_weight(),
    bgn: unit_degrees_as_pixels(rotationxy[1], pw),
    min: unit_degrees_as_pixels(-90, pw),
    max: unit_degrees_as_pixels(90, pw)
  } ])
}

const getsky = (THREE, pg, projection) => {
  const skygeometry = pg.getskygeometry(THREE, projection)
  const skymaterial = new THREE.MeshBasicMaterial({
    // map   : texture
    side: THREE.FrontSide
  })

  return pg.getscenemesh(THREE, skygeometry, skymaterial)
}

const getscene = (ss, cfg, gr, nd, pg, rafcfg, subj) => {
  // const glcanvaselem = pg.getresetglcanvas(cfg, nd)
  // const glcanvaselemwh = domwh(glcanvaselem)
  // const pw = pg.pixelweight(glcanvaselem)
  // const glscene = new THREE.Scene();
  // const camera = new THREE.PerspectiveCamera(
  //   75, glcanvaselemwh[0] / glcanvaselemwh[1], 0.1, 1000);
  //
  // const glrenderer = pg.getglrenderer(THREE, glcanvaselem)
  // glrenderer.setSize(glcanvaselemwh[0], glcanvaselemwh[1]);
  //
  // const geometry = new THREE.BoxGeometry(1, 1, 1);
  // const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
  // const cube = new THREE.Mesh(geometry, material);
  //
  // glscene.add(cube);
  //
  // camera.position.z = 5;
  //
  // glrenderer.render(glscene, camera);

  const THREE = cfg.THREE
  const glcanvaselem = getresetglcanvas(cfg, nd)
  const glscene = new THREE.Scene()
  const glrenderer = getglrenderer(THREE, glcanvaselem)
  const camera = getperspectivecamera(THREE, glcanvaselem)
  const yawbh = getyawobj(cfg.THREE, camera)
  const bodygroup = yawbh[0]
  const headgroup = yawbh[1]
  const skymesh = pg.getsky(THREE, pg, subj.projection)
  const pw = unit_pixel_weightelem(glcanvaselem)

  if (cfg.edgehelper || rafcfg.isskyedge)
    skymesh.add(uimesh.getedgehelper(cfg.THREE, skymesh))

  if (subj.scenetype !== 'architectonic') {
    glscene.add(skymesh)
  }

  glscene.add(bodygroup)
  glscene.colliders = []

  pg.applyscenerotation({
    camera,
    headgroup,
    bodygroup,
    rotationxy: subj.rotationxy
  })

  glrenderer.render(glscene, camera)

  return {
    raycaster: new THREE.Raycaster(),
    lastintersected: null,
    mouse: new THREE.Vector2(),
    projection: subj.projection,
    coords: pg.getcoords(cfg, nd, pg, subj.rotationxy),
    wharr: domwh(glcanvaselem),
    bodygroup,
    headgroup,
    glrenderer,
    glcanvaselem,
    camera,
    glscene,
    pw
  }
}

const setmousexy = (sess, cfg, graph, node, rafcfg, coordarr) => {
  const mouse = rafcfg.mouse
  const canvaselem = gndom_nodeelem(cfg, node, 'glcanvas')

  if (mouse && canvaselem) {
    const rect = canvaselem.getBoundingClientRect()
    const offsetx = rect.width / 2
    // offsety = rect.height / 2,
    const x = coordarr[0] - rect.left - offsetx
    // y = coordarr[1] - rect.top + offsety;

    mouse.x = x / (rect.width / 2)
    mouse.y = -((coordarr[1] - rect.top) / (rect.bottom - rect.top)) * 2 + 1
  }

  return rafcfg
}

const rebuildscene = (ss, cfg, gr, nd, pg, rafcfg, subj) => {
  disposeNode(cfg.THREE, rafcfg.glscene)

  delete rafcfg.glscene

  rafcfg = Object.assign(rafcfg, subj)
  rafcfg.wharr = getglcanvaswh(cfg, nd)
  rafcfg = Object.assign(
    rafcfg, pg.getscene(ss, cfg, gr, nd, pg, rafcfg, subj))

  rafcfg = pg.addtosceneseqactions(ss, cfg, gr, nd, rafcfg)

  return rafcfg
}

const applyscenerotation = ({ camera, headgroup, rotationxy }) => {
  if (Array.isArray(rotationxy) && camera) {
    camera.rotation.x = unit_degrees_as_radians(rotationxy[1])
    headgroup.rotation.y = unit_degrees_as_radians(rotationxy[0])
  }
}

// /databgscene has no dataoutarr
const applyscenerotationpatch = (ss, cfg, gr, nd, prev, rotation) => {
  applyscenerotation({
    camera: prev.camera,
    headgroup: prev.headgroup,
    bodygroup: prev.bodygroup,
    rotationxy: rotation.rotationxy
  })

  gnpublish_subj(ss, cfg, nd.get('key'), rotation)
}

const xonattach = (ss, cfg, gr, nd, pg, prev, subj) => {
  const canvaselem = getresetglcanvas(cfg, nd)

  prev = pg.initialstagestate(ss, cfg, gr, nd, prev)
  prev = touchboom.attach(prev, canvaselem, {
    oneventfn: (prev, etype, e) => {
      if (etype === 'start') {
        canvaselem.focus()

        pg.onstagemousedown(ss, cfg, gr, nd, prev, e)
      }

      if (etype === 'end' ||
          etype === 'cancel' ||
          etype === 'out') {
        pg.applyscenerotationpatch(ss, cfg, gr, nd, prev, (
          pg.getlocalrotation(ss, cfg, gr, nd, prev)
        ))

        pg.onstagemouseup(ss, cfg, gr, nd, prev, e)

        if (etype === 'out') {
          pg.setmousexy(ss, cfg, gr, nd, prev, [ -6000, -6000 ])
          // o.setmousexy(sess, cfg, graph, node, prev, touchboom.getevxy(e));
        }
      }
    },

    oninertiafn: prev => {
      pg.applyscenerotationpatch(ss, cfg, gr, nd, prev, (
        pg.getlocalrotation(ss, cfg, gr, nd, prev)
      ))
    },

    onmovefn: (prev, etype, e) => {
      const evxy = touchboom.getevxy(e)

      pg.setmousexy(ss, cfg, gr, nd, prev, evxy)
    }
  })

  prev.wharr = getglcanvaswh(cfg, nd)
  prev = pg.rebuildscene(ss, cfg, gr, nd, pg, prev, subj)
}

const xonblur = (sess, cfg, graph, node, pg, prev) => (
  touchboom.coordsmoveend(prev))

const xonfocusout = (sess, cfg, graph, node, pg, prev) => (
  touchboom.coordsmoveend(prev))

const xonresize = (ss, cfg, gr, nd, pg, prev, subj) => {
  pg.resetcanvas(pg.getglcanvas(cfg, nd))

  if (pg.isresized(ss, cfg, gr, nd, prev)) {
    pg.rebuildscene(ss, cfg, gr, nd, pg, prev, subj)
  }
}

const xonframe = (ss, cfg, gr, nd, pg, prev) => {
  if (prev.glscene) {
    pg.updatechildseq(ss, cfg, gr, nd, prev)
    uicanvasstage.raycheck(ss, cfg, gr, nd, pg, prev)
    // pg.render(prev)
    render(prev)
  }
}

const xonsubj = (ss, cfg, gr, nd, pg, prev, subj) => {
  const rotationxy = subj.rotationxy
  const projection = subj.projection
  const isvrscreen = subj.isvrscreen

  if (!prev.glscene) {
    // return true and avoid triggering rerender here
    return true
  }

  if (prev.isvrscreen !== isvrscreen) {
    prev.isvrscreen = isvrscreen
  } else if (prev.projection !== projection) {
    prev.projection = String(projection)

    pg.rebuildscene(ss, cfg, gr, nd, pg, prev, subj)
  } else if (!rotationxyIsSame(prev.rotationxy, rotationxy)) {
    prev.rotationxy = rotationxy.slice()

    pg.applyscenerotation({
      camera: prev.camera,
      headgroup: prev.headgroup,
      bodygroup: prev.bodygroup,
      rotationxy
    })
  }

  if (pg.isresized(ss, cfg, gr, nd, prev)) {
    pg.rebuildscene(ss, cfg, gr, nd, pg, prev, subj)
  }

  return true
}

export default Object.assign({}, canv, uicanvasstage, {
  type: 'uicanvasportal',
  xvnode,
  xonattach,
  xonblur,
  xonfocusout,
  xonresize,
  xonframe,
  xonsubj,
  getscene,
  getsky,
  getskygeometry,
  getscenemesh,
  getcoords,
  setmousexy,
  getlocalrotation,
  rebuildscene,
  isresized,
  applyscenerotation,
  applyscenerotationpatch
})
